import { Moment } from 'moment-timezone';

import { InsightDateRange } from 'containers/Intelligence/filters/useInsightFilters';
import { decodeFilterValue } from 'containers/Intelligence/registry/filters';
import { IntelligenceFilterId, IntelligenceFilters } from 'containers/Intelligence/types';

/**
 * The ONE place the Customer Insights filter vocabulary lives.
 *
 * It is one place because of a property of the endpoint, not for tidiness: perx-api forwards query
 * parameters to Metabase verbatim and validates nothing, so **there is no 400 on this route**. A
 * misspelled key is not rejected — it reaches Metabase, fails there, and returns a 502 that is
 * indistinguishable from a real outage. The backend will never tell us a name is wrong, so a second
 * copy of these strings anywhere would be a defect nothing could detect.
 *
 * Keys are the agreed set in `features/PLA-8815/plan/catalogue-contract-reconciliation.md` § 6.3.
 * The VALUES are not minted here — every option id already ships in `registry/filters.ts` and maps
 * 1:1 onto the catalogue's option labels. Only the keys are new. PLA-8815, contract PLA-8817.
 *
 * NO `with_wtr` KEY. Probed on UAT 2026-09-14: it is a no-op where the question declares it and a
 * 502 (`50201`) on `C-COM-009`, `C-HYB-006` and `O-COM-005`, which do not — and the `wtr_*` columns
 * arrive in the baseline response either way. PLA-8828.
 */

// Panel category -> wire key. `purchaseBehaviour` and `engagement` are absent on purpose, not by
// omission: no card across the 211-card perx3_agentic_ai catalogue declares `purchase_behaviour`,
// `engagement_level`, or any variable for either — confirmed 2026-08-28. Mapping them to a wire key
// nothing reads would be exactly the "reaches Metabase, fails there" 502 this file's own docstring
// warns about. Demographics is absent for a different reason: its key IS the chosen dimension
// (`age_band`, `gender`, …), so it cannot be a fixed mapping — see `DEMOGRAPHIC_DIMENSIONS` below.
// `consentStatus` is absent for a different reason still, below.
const FILTER_KEYS: Partial<Record<IntelligenceFilterId, string>> = {
  lifecycle: 'lifecycle_stage',
  customerValue: 'customer_value_band',
  productRelationship: 'product_relationship'
};

// Demographic dimension -> wire key. Only two of the panel's five dimensions have a real Metabase
// variable behind them today (confirmed against all 211 perx3_agentic_ai cards, 2026-08-28):
// `income_band` shares its name on both sides, but `age_band` does not — Metabase's cards declare
// `birth_year_band`, not `age_band`, so the UI's own option id cannot double as the wire key here the
// way it does for income_band. `gender`, `nationality`, `ethnicity` stay valid, selectable options in
// `registry/filters.ts` (unrelated FE/FR surface, not touched here) but have no card variable to
// write to yet, so selecting them narrows nothing on the wire — the same silent no-op this file
// already gives `consentStatus`, not an error.
const DEMOGRAPHIC_DIMENSIONS: Partial<Record<string, string>> = {
  age_band: 'birth_year_band',
  income_band: 'income_band'
};

// Default option ids. They are FE presentation values meaning "no narrowing on this dimension" and
// MUST NOT reach the wire: an absent key is what a backend reads as "no narrowing", while a literal
// `all` would be read as a value nobody defined.
const DEFAULT_OPTION_IDS = ['all', 'all_permitted'];

const isDefault = (optionId: string): boolean =>
  !optionId || DEFAULT_OPTION_IDS.indexOf(optionId) !== -1;

// Demographics carries a dimension AND a value in one encoded string (`age_band:25-34`), so both
// halves have to be present before anything is sent. A dimension with no value would narrow on a
// field without saying to what.
const demographicParam = (value?: string): Record<string, string> => {
  const { optionId, subValue } = decodeFilterValue(value);
  const wireKey = DEMOGRAPHIC_DIMENSIONS[optionId];

  if (isDefault(optionId) || !subValue || !wireKey) {
    return {};
  }

  return { [wireKey]: subValue };
};

/**
 * The comparison-period half of the date pair, named because a consumer has to be able to REMOVE it:
 * `data/liveCardSource.ts` drops these two keys for the Product Readiness metrics, whose Metabase
 * cards 502 on them. Every other key in this file is a literal because nothing outside reads it.
 * PLA-8815.
 */
export const PREVIOUS_START_DATE_KEY = 'previous_start_date';
export const PREVIOUS_END_DATE_KEY = 'previous_end_date';

export interface MetricQueryWindows {
  /** `YYYY-MM-DD HH:mm:ss`, UTC. */
  startDate: string;
  /** Same format; EXCLUSIVE — the window is half-open `[start, end)`. */
  endDate: string;
  /** Omitted together for "No comparison"; `previousEndDate` equals `startDate` when contiguous. */
  previousStartDate?: string;
  previousEndDate?: string;
}

/**
 * The query for one metric request.
 *
 * There is no `date_range` key and no `comparison_period` key, deliberately. All five date presets
 * — custom included — resolve on this side into `start_date`/`end_date`, and all four comparison
 * options into the previous pair or its absence, so the backend never learns which preset was
 * picked. `comparison_period` becomes a key only if narratives are ever composed server-side.
 *
 * `consent_status` is never emitted. Letting the client state a consent filter is letting the client
 * make an authorisation decision, and the catalogue says the same from the other side: the filter
 * "cannot override consent enforcement". If consent narrowing is ever wanted it has to be derived
 * from the token, which means it is not this function's output.
 */
export const toMetricQuery = (
  filters: IntelligenceFilters,
  windows: MetricQueryWindows
): Record<string, string> => {
  const query: Record<string, string> = {
    start_date: windows.startDate,
    end_date: windows.endDate
  };

  if (windows.previousStartDate && windows.previousEndDate) {
    query[PREVIOUS_START_DATE_KEY] = windows.previousStartDate;
    query[PREVIOUS_END_DATE_KEY] = windows.previousEndDate;
  }

  (Object.keys(FILTER_KEYS) as IntelligenceFilterId[]).forEach(categoryId => {
    const { optionId } = decodeFilterValue(filters?.[categoryId]);

    if (!isDefault(optionId)) {
      query[FILTER_KEYS[categoryId] as string] = optionId;
    }
  });

  return { ...query, ...demographicParam(filters?.demographics) };
};

/**
 * Whether a metric request may carry the query above.
 *
 * `false`, and not as caution. The Layer 2 card SQL declares only `{{tenant_id}}` and `metric_id`,
 * so no Layer 2 card CONSUMES a date or filter parameter yet, and whether Metabase rejects a
 * parameter its card does not declare is unverified. If it does, every filtered request 502s — and
 * with no 400 on this route we could not tell that from an outage. The no-parameter path is the one
 * dev-tested 121/121 on UAT.
 *
 * Flipping this to `true` is the whole change, once one card has been tested with one parameter.
 * PLA-8815, plan § V.2 and the backend draft § 4.
 */
export const METRIC_QUERY_ENABLED = false;

export const metricQueryFor = (
  filters: IntelligenceFilters,
  windows: MetricQueryWindows
): Record<string, string> => (METRIC_QUERY_ENABLED ? toMetricQuery(filters, windows) : {});

const QUERY_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const toWireInstant = (value: Moment): string => value.clone().utc().format(QUERY_DATE_FORMAT);

// `InsightDateRange.end` is the day's last inclusive instant (`endOf('day')`), but the wire window
// is half-open `[start, end)`, so the exclusive boundary is one millisecond later.
const toExclusiveEnd = (end: Moment): string =>
  end.clone().add(1, 'millisecond').utc().format(QUERY_DATE_FORMAT);

export const windowsFromDateRange = (
  dateRange: InsightDateRange,
  comparisonRange: InsightDateRange
): MetricQueryWindows => ({
  startDate: toWireInstant(dateRange.start),
  endDate: toExclusiveEnd(dateRange.end),
  previousStartDate: toWireInstant(comparisonRange.start),
  previousEndDate: toExclusiveEnd(comparisonRange.end)
});

/**
 * The date pair(s) only — unconditional, unlike `metricQueryFor`. Passing an empty `filters` into
 * `toMetricQuery` yields exactly `start_date`/`end_date`/`previous_*` and nothing else, because every
 * category key it emits is conditional on a non-default selection. The five category keys and
 * demographics stay behind `METRIC_QUERY_ENABLED` until a Layer 2 card declares those parameters.
 */
export const dateQueryFor = (
  dateRange: InsightDateRange,
  comparisonRange: InsightDateRange
): Record<string, string> => toMetricQuery({}, windowsFromDateRange(dateRange, comparisonRange));
